import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ToastPayload, ToastVariant } from '../utils/toast';
import type { MicRationaleDetail } from '../utils/microphoneRationale';
import { ConfirmDialog } from './ConfirmDialog';
import { t } from '../i18n';

type ToastItem = ToastPayload & { id: string };

type ToastContextValue = {
  pushToast: (toast: ToastPayload) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 4000;

function variantClasses(variant: ToastVariant | undefined) {
  switch (variant) {
    case 'success':
      return 'bg-green-600 text-white';
    case 'warning':
      return 'bg-yellow-500 text-black';
    case 'error':
      return 'bg-red-600 text-white';
    default:
      return 'bg-gray-900 text-white';
  }
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [micRationale, setMicRationale] = useState<MicRationaleDetail | null>(null);

  const pushToast = useCallback((toast: ToastPayload) => {
    const id = crypto.randomUUID();
    const item: ToastItem = { id, ...toast };
    setToasts((prev) => [...prev, item]);
    const duration = toast.durationMs ?? DEFAULT_DURATION;
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ToastPayload>).detail;
      if (detail?.message) {
        pushToast(detail);
      }
    };

    window.addEventListener('app:toast', handler);
    return () => window.removeEventListener('app:toast', handler);
  }, [pushToast]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<MicRationaleDetail>).detail;
      if (detail) {
        setMicRationale(detail);
      }
    };
    window.addEventListener('app:mic-rationale', handler);
    return () => window.removeEventListener('app:mic-rationale', handler);
  }, []);

  const value = useMemo(() => ({ pushToast }), [pushToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={micRationale !== null}
        title={t('micRationale.title')}
        description={t('micRationale.description')}
        confirmText={t('micRationale.confirm')}
        cancelText={t('common.cancel')}
        onConfirm={() => {
          const cb = micRationale;
          setMicRationale(null);
          cb?.resolve();
        }}
        onCancel={() => {
          const cb = micRationale;
          setMicRationale(null);
          cb?.reject();
        }}
      />
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`rounded px-4 py-2 text-sm shadow-lg ${variantClasses(toast.variant)}`}
          >
            <div className="flex items-center gap-3">
              <span className="flex-1">{toast.message}</span>
              {toast.actionLabel && toast.onAction && (
                <button
                  type="button"
                  className="text-xs underline underline-offset-2 font-medium"
                  onClick={() => {
                    try {
                      toast.onAction?.();
                    } finally {
                      dismissToast(toast.id);
                    }
                  }}
                >
                  {toast.actionLabel}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
